import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Plus, 
    FolderOpen, 
    Trash2, 
    X,
    Clock,
    FileCode
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useProjects } from '../context/ProjectContext';

export const ProjectSidebar = ({ isOpen, onClose, onNewProject }) => {
    const { projects, currentProject, loadProject, deleteProject } = useProjects();

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        
        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return date.toLocaleDateString();
    };

    const truncateText = (text, maxLength) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <>
            {/* Mobile overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 z-40 md:hidden"
                        onClick={onClose}
                    />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <motion.aside
                initial={false}
                animate={{ 
                    x: isOpen ? 0 : -280,
                    opacity: isOpen ? 1 : 0
                }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className={`fixed md:relative left-0 top-0 h-full w-[280px] sidebar z-50 flex flex-col ${isOpen ? '' : 'md:hidden'}`}
                data-testid="project-sidebar"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/5">
                    <h2 className="font-heading font-semibold text-lg">Projects</h2>
                    <div className="flex items-center gap-2">
                        <Button
                            data-testid="new-project-btn"
                            variant="ghost"
                            size="icon"
                            onClick={onNewProject}
                            className="h-8 w-8 hover:bg-primary/20"
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="h-8 w-8 md:hidden"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Projects list */}
                <ScrollArea className="flex-1">
                    <div className="p-3 space-y-2">
                        {projects.length === 0 ? (
                            <div className="text-center py-12 px-4">
                                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                    <FileCode className="w-6 h-6 text-gray-500" />
                                </div>
                                <p className="text-gray-500 text-sm">
                                    No projects yet. Generate your first blueprint!
                                </p>
                            </div>
                        ) : (
                            projects.map((project) => (
                                <motion.div
                                    key={project.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`project-item p-3 rounded-lg cursor-pointer group ${
                                        currentProject?.id === project.id 
                                            ? 'bg-primary/20 border border-primary/30' 
                                            : 'hover:bg-white/5'
                                    }`}
                                    onClick={() => loadProject(project.id)}
                                    data-testid={`project-item-${project.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-medium text-sm text-white truncate">
                                                {project.blueprint?.app_name || 'Untitled Project'}
                                            </h3>
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                                {truncateText(project.idea, 60)}
                                            </p>
                                            <div className="flex items-center gap-1 mt-2 text-xs text-gray-600">
                                                <Clock className="w-3 h-3" />
                                                {formatDate(project.createdAt)}
                                            </div>
                                        </div>
                                        <Button
                                            data-testid={`delete-project-${project.id}`}
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:bg-destructive/20 hover:text-destructive transition-opacity"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteProject(project.id);
                                            }}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </div>
                </ScrollArea>

                {/* Footer */}
                <div className="p-4 border-t border-white/5">
                    <p className="text-xs text-gray-600 text-center">
                        {projects.length} project{projects.length !== 1 ? 's' : ''} saved locally
                    </p>
                </div>
            </motion.aside>
        </>
    );
};
